<!--
    Deal-detail page for traffic arriving from the dealswidget.com embed
    plugin. Distinct from the legacy /deal/:id page (Details.vue) because:

    - Data source is DealsDB.DealsRaw via /api/embed/deal/:id, NOT
      MommyDealsDB.tblDeals via /api/GetDeal.
    - Affiliate-tag rewriting uses ascsubtag=partner_<id> for per-partner
      Amazon Associates attribution, mirroring the partner_ branch added
      to DealBox.vue.
    - The page renders the AI-generated EmbedImgUrl when available;
      otherwise falls back to the source Amazon image (this page is on
      mommydeals.us — our registered Amazon Associates site — so showing
      the Amazon image here is allowed under the Operating Agreement).
    - No related-deals widget yet (would require a DealsDB equivalent of
      the existing Deals.vue — out of scope for v1).

    Server route: AppController.cs DwDeal() at /dw/deal/{id} sets
    MMD_LastRef cookie from ?ref= via RecordVisitor() before this Vue
    view loads.
-->
<template>
    <div class="dw-page">
        <div v-if="loading" class="dw-page-loading">Loading deal…</div>

        <div v-else-if="error" class="dw-page-error">
            <h1>Deal not found</h1>
            <p>This deal may have expired or is no longer available.</p>
            <a href="https://mommydeals.us">Back to MommyDeals</a>
        </div>

        <div v-else-if="deal" class="dw-page-card">
            <div class="dw-page-image">
                <img v-if="displayImg"
                     :src="displayImg"
                     :alt="deal.title" />
            </div>

            <div class="dw-page-info">
                <div v-if="deal.brand" class="dw-page-brand">{{ deal.brand }}</div>
                <h1 class="dw-page-title">{{ deal.title }}</h1>

                <div v-if="deal.dealPrice != null" class="dw-page-price-row">
                    <span v-if="hasStrike" class="dw-page-strike">{{ formatPrice(deal.retailPrice) }}</span>
                    <span class="dw-page-price">{{ formatPrice(deal.dealPrice) }}</span>
                    <span v-if="discount" class="dw-page-discount">-{{ discount }}%</span>
                </div>

                <a v-if="amazonHref"
                   :href="amazonHref"
                   target="_blank"
                   rel="noopener noreferrer"
                   class="dw-page-cta"
                   @click="onAmazonClick">
                    Get this deal on {{ deal.dealProvider || 'Amazon' }}
                </a>

                <div v-if="deal.description" class="dw-page-desc">
                    {{ deal.description }}
                </div>

                <div class="dw-page-disclosure">
                    Disclosure: MommyDeals is a participant in the Amazon Services LLC
                    Associates Program. As an Amazon Associate we earn from qualifying
                    purchases. Prices and availability are accurate as of the time
                    indicated and may change without notice.
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Cookies from 'js-cookie'
import util from '@/helpers/util'
import request from '@/helpers/request'

interface EmbedDealDetail {
    dealId: number
    title: string
    description: string | null
    brand: string | null
    asin: string | null
    retailPrice: number | null
    dealPrice: number | null
    dealUrl: string | null
    outboundUrl: string | null
    dealProvider: string | null
    sourceImgUrl: string | null
    embedImgUrl: string | null
    prettyTitle: string
}

const route = useRoute()
const deal = ref<EmbedDealDetail | null>(null)
const loading = ref(true)
const error = ref(false)

// Prefer the AI-generated img2img CDN image (consistent with what the user
// just clicked in the embed). Fall back to the source Amazon image since
// this page is on mommydeals.us — our registered Associates site — so the
// Amazon image is allowed here.
const displayImg = computed(() => deal.value?.embedImgUrl || deal.value?.sourceImgUrl || null)

const hasStrike = computed(() =>
    deal.value?.retailPrice != null
    && deal.value?.dealPrice != null
    && deal.value.retailPrice > deal.value.dealPrice
)

const discount = computed(() => {
    if (!deal.value) return null
    const r = deal.value.retailPrice, d = deal.value.dealPrice
    if (r == null || d == null || r <= 0 || d >= r) return null
    return Math.round(((r - d) / r) * 100)
})

// Build the affiliate URL exactly like DealBox.vue's onMounted block does
// (lines 246-275 of DealBox.vue), but only the partner_ branch — we know
// traffic to /dw/deal/:id arrived via an embed click, so the cookie is
// always partner-shaped.
const amazonHref = computed(() => {
    if (!deal.value?.outboundUrl) return null
    let url = deal.value.outboundUrl
    const cookieRef = Cookies.get('MMD_LastRef')

    if (cookieRef && cookieRef.toLowerCase().startsWith('partner_')) {
        if (deal.value.dealProvider?.toLowerCase() === 'amazon') {
            url = util.replaceOrAddUrlParam(url, 'tag', 'mommydeals0a-20')
            url = util.replaceOrAddUrlParam(url, 'ascsubtag', cookieRef)
        }
        else if (deal.value.dealProvider?.toLowerCase() === 'walmart') {
            url = util.replaceOrAddUrlParam(url, 'sharedid', cookieRef)
        }
    }
    else if (deal.value.dealProvider?.toLowerCase() === 'amazon') {
        // Direct visit (no partner cookie) — still a MommyDeals click, use
        // the master tag so we earn the affiliate commission ourselves.
        url = util.replaceOrAddUrlParam(url, 'tag', 'mommydeals0a-20')
    }

    return url
})

function formatPrice(n: number | null): string {
    if (n == null) return ''
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(n)
    } catch {
        return `$${n.toFixed(2)}`
    }
}

function onAmazonClick() {
    // Browser handles the navigation via target="_blank"; this hook is a
    // future tap-point for adding click telemetry on the detail page if
    // we want to distinguish "saw the page" from "clicked through".
}

onMounted(async () => {
    const rawId = String(route.params.id || '')
    const id = Number.parseInt(rawId, 10)
    if (!Number.isFinite(id) || id <= 0) {
        error.value = true
        loading.value = false
        return
    }
    try {
        const res = await request.get(`/api/embed/deal/${id}`)
        deal.value = res.data as EmbedDealDetail
        loading.value = false
    } catch (e) {
        console.error('failed to load embed deal', e)
        error.value = true
        loading.value = false
    }
})
</script>

<style scoped>
.dw-page {
    max-width: 1100px;
    margin: 0 auto;
    padding: 24px 16px 48px;
}

.dw-page-loading,
.dw-page-error {
    text-align: center;
    padding: 64px 16px;
    color: #6b6b73;
}

.dw-page-card {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

@media (max-width: 768px) {
    .dw-page-card {
        grid-template-columns: 1fr;
        padding: 16px;
        gap: 16px;
    }
}

.dw-page-image {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
    background: #fafafa;
    border-radius: 8px;
    overflow: hidden;
}
.dw-page-image img {
    max-width: 100%;
    max-height: 480px;
    object-fit: contain;
}

.dw-page-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.dw-page-brand {
    font-size: 13px;
    color: #6b6b73;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.dw-page-title {
    font-size: 22px;
    line-height: 1.3;
    margin: 0;
    color: #1f1f24;
    font-weight: 600;
}

.dw-page-price-row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-top: 4px;
}
.dw-page-price {
    font-size: 28px;
    font-weight: 700;
    color: #1f1f24;
}
.dw-page-strike {
    font-size: 16px;
    color: #a1a1a8;
    text-decoration: line-through;
}
.dw-page-discount {
    background: #ff4f64;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 4px;
}

.dw-page-cta {
    display: inline-block;
    background: #ff9100;
    color: #fff;
    font-weight: 700;
    font-size: 16px;
    text-decoration: none;
    padding: 14px 24px;
    border-radius: 999px;
    text-align: center;
    margin-top: 8px;
    transition: background .15s ease;
}
.dw-page-cta:hover {
    background: #e57e00;
}

.dw-page-desc {
    color: #4a4a52;
    line-height: 1.6;
    font-size: 14px;
    margin-top: 8px;
}

.dw-page-disclosure {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #ececef;
    color: #9a9aa0;
    font-size: 11px;
    line-height: 1.5;
}
</style>
